import React, { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Upload, Camera, X, Loader2, 
  CheckCircle2, AlertCircle, ImageIcon,
  Plus, GripVertical, ArrowUp, ArrowDown
} from "lucide-react";

const HandwrittenUpload = ({ 
  marks = 10, 
  suite = "GS2",
  onEvaluationComplete,
  onExtractedData,
  disabled = false 
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState([]); // Array of {file, previewUrl, id}
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState("");

  const MAX_IMAGES = 10; // Maximum pages allowed
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per image

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Check total count
    if (selectedImages.length + files.length > MAX_IMAGES) {
      toast({
        variant: "destructive",
        description: `Maximum ${MAX_IMAGES} pages allowed. You can add ${MAX_IMAGES - selectedImages.length} more.`
      });
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    const newImages = [];

    for (const file of files) {
      // Validate file type
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          description: `${file.name}: Invalid format. Use JPG, PNG, or WebP only.`
        });
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast({
          variant: "destructive",
          description: `${file.name}: Too large. Max 10MB per image.`
        });
        continue;
      }

      newImages.push({
        id: Date.now() + Math.random(),
        file,
        previewUrl: URL.createObjectURL(file)
      });
    }

    if (newImages.length > 0) {
      setSelectedImages(prev => [...prev, ...newImages]);
      setExtractedData(null);
      toast({
        description: `✅ Added ${newImages.length} page(s). Total: ${selectedImages.length + newImages.length} pages.`
      });
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = (id) => {
    setSelectedImages(prev => {
      const updated = prev.filter(img => img.id !== id);
      return updated;
    });
    setExtractedData(null);
  };

  const handleMoveImage = (index, direction) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= newImages.length) return prev;
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      return newImages;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const syntheticEvent = { target: { files: files } };
      handleFileSelect(syntheticEvent);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUploadAndEvaluate = async () => {
    if (selectedImages.length === 0) {
      toast({
        variant: "destructive",
        description: "Please upload at least one image of your handwritten answer."
      });
      return;
    }

    setUploading(true);
    setUploadProgress("Uploading images...");
    
    try {
      const formData = new FormData();
      
      // Append images in order
      selectedImages.forEach((img, index) => {
        formData.append("images", img.file);
      });
      
      formData.append("marks", marks.toString());
      formData.append("suite", suite);
      formData.append("page_count", selectedImages.length.toString());

      setUploadProgress(`Processing ${selectedImages.length} page(s)...`);

      const { data } = await axios.post(`${API_BASE}/eval/handwritten-multi`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 180000, // 3 minute timeout for multi-page OCR + evaluation
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(`Uploading: ${percent}%`);
        }
      });

      setUploadProgress("");

      // Store extracted data
      setExtractedData({
        question: data.extracted_question,
        answer: data.extracted_answer,
        pages_processed: data.pages_processed
      });
      
      if (onExtractedData) {
        onExtractedData({
          question: data.extracted_question,
          answer: data.extracted_answer
        });
      }

      // Pass evaluation to parent
      if (onEvaluationComplete) {
        onEvaluationComplete(data);
      }

      toast({
        description: `✅ ${data.pages_processed || selectedImages.length} page(s) processed and evaluated!`
      });

    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to process handwritten answer.";
      toast({
        variant: "destructive",
        description: msg
      });
      setUploadProgress("");
    } finally {
      setUploading(false);
    }
  };

  const clearAllImages = () => {
    setSelectedImages([]);
    setExtractedData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div 
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
          ${selectedImages.length > 0 
            ? 'border-indigo-400 bg-indigo-50/30' 
            : 'border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={() => !disabled && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
          multiple={true}
        />

        {selectedImages.length === 0 ? (
          <div className="space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
              <Camera className="w-8 h-8 text-indigo-500" />
            </div>
            <div>
              <p className="font-semibold text-slate-700">Upload Handwritten Answer Pages</p>
              <p className="text-sm text-slate-500">
                Click or drag to upload photos of your answer sheets
              </p>
              <p className="text-xs text-emerald-600 font-medium mt-1">
                📄 Multiple pages supported • Upload in order (Page 1, 2, 3...)
              </p>
              <p className="text-xs text-slate-400 mt-1">
                AI will extract & combine text from all pages • JPG, PNG, WebP • Max 10MB each
              </p>
            </div>
            <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <ImageIcon className="w-4 h-4" />
                Up to {MAX_IMAGES} pages
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Good lighting
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-2 text-indigo-600 mb-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">{selectedImages.length} page(s) selected</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-indigo-600 border-indigo-300"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add More Pages
            </Button>
          </div>
        )}
      </div>

      {/* Image Previews with Ordering */}
      {selectedImages.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">
              📑 Pages (in order)
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={clearAllImages}
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {selectedImages.map((img, index) => (
              <div 
                key={img.id} 
                className="relative group bg-white rounded-lg border border-slate-200 p-2 shadow-sm"
              >
                {/* Page Number Badge */}
                <Badge 
                  className="absolute -top-2 -left-2 z-10 bg-indigo-600 text-white"
                >
                  Page {index + 1}
                </Badge>
                
                {/* Image Preview */}
                <img
                  src={img.previewUrl}
                  alt={`Page ${index + 1}`}
                  className="w-full h-24 object-cover rounded"
                />
                
                {/* Controls */}
                <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="w-6 h-6 rounded-full"
                    onClick={() => handleRemoveImage(img.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                
                {/* Reorder Buttons */}
                {selectedImages.length > 1 && (
                  <div className="flex justify-center gap-1 mt-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="w-6 h-6"
                      disabled={index === 0}
                      onClick={() => handleMoveImage(index, 'up')}
                    >
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="w-6 h-6"
                      disabled={index === selectedImages.length - 1}
                      onClick={() => handleMoveImage(index, 'down')}
                    >
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                
                {/* File name */}
                <p className="text-[10px] text-slate-500 truncate mt-1 text-center">
                  {img.file.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extracted Data Preview */}
      {extractedData && (
        <div className="space-y-3">
          {extractedData.pages_processed && (
            <Badge variant="outline" className="text-emerald-700 border-emerald-300">
              ✅ {extractedData.pages_processed} pages processed
            </Badge>
          )}
          
          {extractedData.question && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-blue-700 border-blue-300">
                    📝 Extracted Question
                  </Badge>
                </div>
                <p className="text-sm text-slate-700 font-medium">
                  {extractedData.question}
                </p>
              </CardContent>
            </Card>
          )}
          
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                  ✍️ Extracted Answer (Combined)
                </Badge>
                <span className="text-xs text-slate-500">
                  ({extractedData.answer?.length || 0} characters)
                </span>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap max-h-96 overflow-y-auto">
                {extractedData.answer}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Evaluate Button */}
      {selectedImages.length > 0 && (
        <Button
          onClick={handleUploadAndEvaluate}
          disabled={uploading || disabled}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {uploadProgress || "Processing..."}
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Extract & Evaluate {selectedImages.length} Page(s)
            </>
          )}
        </Button>
      )}

      {/* Tips */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-800">
            <p className="font-semibold mb-1">Tips for best results:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Upload pages in correct order (1, 2, 3...)</li>
              <li>Include the question on the first page</li>
              <li>Use good lighting - avoid shadows</li>
              <li>Keep handwriting legible</li>
              <li>Capture full page without cropping</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandwrittenUpload;
